import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Database, Crown, Loader2, Trash2, MoreVertical, Shield, UserX, Eye, Globe, Plus, KeyRound, Save, Settings as SettingsIcon, UserPlus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded border border-border/40 bg-card/40 p-3.5 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-muted-foreground">
          {label}
        </span>
        <Icon className={`h-3 w-3 ${accent || 'text-primary/70'}`} />
      </div>
      <div className="font-heading text-xl font-bold leading-none">{value}</div>
    </div>
  );
}

function PlanBadge({ plan }) {
  const isPremium = plan === 'premium';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[9px] tracking-wider uppercase ${
        isPremium
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border/50 bg-background/40 text-muted-foreground'
      }`}
    >
      <span className={`w-1 h-1 rounded-full ${isPremium ? 'bg-primary animate-pulse-dot' : 'bg-muted-foreground/40'}`} />
      {plan}
    </span>
  );
}

function RoleBadge({ role }) {
  const isAdmin = role === 'admin';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[9px] tracking-wider uppercase ${
        isAdmin
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'border-border/50 bg-background/40 text-muted-foreground'
      }`}
    >
      {isAdmin && <Shield className="h-2.5 w-2.5" />}
      {role}
    </span>
  );
}

export default function Admin() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Records viewer
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [recordsUser, setRecordsUser] = useState(null);
  const [userRecords, setUserRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [deleteRecordOpen, setDeleteRecordOpen] = useState(false);
  const [deleteRecordItem, setDeleteRecordItem] = useState(null);
  const [deleteRecordLoading, setDeleteRecordLoading] = useState(false);

  // Domains
  const [activeTab, setActiveTab] = useState('users');
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [addDomainOpen, setAddDomainOpen] = useState(false);
  const [domainForm, setDomainForm] = useState({ name: '', zone_id: '' });
  const [addDomainLoading, setAddDomainLoading] = useState(false);
  const [deleteDomainOpen, setDeleteDomainOpen] = useState(false);
  const [deleteDomainItem, setDeleteDomainItem] = useState(null);
  const [deleteDomainLoading, setDeleteDomainLoading] = useState(false);

  // OAuth settings
  const [oauthForm, setOauthForm] = useState({ enabled: false, client_id: '', client_secret: '' });
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthSaving, setOauthSaving] = useState(false);

  // App settings
  const [appSettings, setAppSettings] = useState({ registration_enabled: true });
  const [appSettingsLoading, setAppSettingsLoading] = useState(false);
  const [appSettingsSaving, setAppSettingsSaving] = useState(false);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: getHeaders() }),
      ]);
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('admin access required');
      } else {
        toast.error('failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const fetchDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/domains`, { headers: getHeaders() });
      setDomains(res.data.domains || []);
    } catch {
      toast.error('failed to load domains');
    } finally {
      setDomainsLoading(false);
    }
  }, [getHeaders]);

  const fetchOauthSettings = useCallback(async () => {
    setOauthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/oauth-settings`, { headers: getHeaders() });
      setOauthForm({
        enabled: !!res.data.enabled,
        client_id: res.data.client_id || '',
        client_secret: res.data.client_secret || '',
      });
    } catch {
      // silently ignore — user may not be admin yet
    } finally {
      setOauthLoading(false);
    }
  }, [getHeaders]);

  const saveOauthSettings = async (e) => {
    e.preventDefault();
    setOauthSaving(true);
    try {
      await axios.put(`${API}/admin/oauth-settings`, oauthForm, { headers: getHeaders() });
      toast.success(t('admin.oauth_saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to save');
    } finally {
      setOauthSaving(false);
    }
  };

  const fetchAppSettings = useCallback(async () => {
    setAppSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers: getHeaders() });
      setAppSettings({ registration_enabled: res.data.registration_enabled !== false });
    } catch {
      // ignore
    } finally {
      setAppSettingsLoading(false);
    }
  }, [getHeaders]);

  const saveAppSettings = async (newValues) => {
    setAppSettingsSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, newValues, { headers: getHeaders() });
      setAppSettings(newValues);
      toast.success(t('admin.settings_saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to save');
    } finally {
      setAppSettingsSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDomains();
    fetchOauthSettings();
    fetchAppSettings();
  }, [fetchData, fetchDomains, fetchOauthSettings, fetchAppSettings]);

  const handleChangePlan = async (userId, plan) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan }, { headers: getHeaders() });
      toast.success(`plan → ${plan}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to update plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteUser.id}`, { headers: getHeaders() });
      toast.success('user deleted');
      setDeleteOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const openUserRecords = async (user) => {
    setRecordsUser(user);
    setRecordsOpen(true);
    setRecordsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${user.id}/records`, { headers: getHeaders() });
      setUserRecords(res.data.records || []);
    } catch {
      toast.error('failed to load user records');
    } finally {
      setRecordsLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!deleteRecordItem) return;
    setDeleteRecordLoading(true);
    try {
      await axios.delete(`${API}/admin/records/${deleteRecordItem.id}`, { headers: getHeaders() });
      toast.success('record deleted');
      setDeleteRecordOpen(false);
      setDeleteRecordItem(null);
      setUserRecords(prev => prev.filter(r => r.id !== deleteRecordItem.id));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to delete record');
    } finally {
      setDeleteRecordLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setAddDomainLoading(true);
    try {
      await axios.post(`${API}/admin/domains`, domainForm, { headers: getHeaders() });
      toast.success('domain added');
      setAddDomainOpen(false);
      setDomainForm({ name: '', zone_id: '' });
      fetchDomains();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to add domain');
    } finally {
      setAddDomainLoading(false);
    }
  };

  const handleToggleDomain = async (domain) => {
    try {
      await axios.put(`${API}/admin/domains/${domain.id}`, { active: !domain.active }, { headers: getHeaders() });
      toast.success(domain.active ? 'domain deactivated' : 'domain activated');
      fetchDomains();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to update domain');
    }
  };

  const handleDeleteDomain = async () => {
    if (!deleteDomainItem) return;
    setDeleteDomainLoading(true);
    try {
      await axios.delete(`${API}/admin/domains/${deleteDomainItem.id}`, { headers: getHeaders() });
      toast.success('domain deleted');
      setDeleteDomainOpen(false);
      setDeleteDomainItem(null);
      fetchDomains();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to delete domain');
    } finally {
      setDeleteDomainLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background page-transition" data-testid="admin-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-12">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="text-[10px] text-primary tracking-wider mb-2">$ sudo --admin</div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="admin-title">
            <Shield className="h-5 w-5 text-primary" />
            {t('admin.title')}
          </h1>
          <p className="text-[11px] text-muted-foreground mt-1 font-mono">// system control panel</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8 animate-fade-in-up delay-100">
            <StatTile icon={Users} label={t('admin.total_users')} value={stats.total_users} />
            <StatTile icon={Database} label={t('admin.total_records')} value={stats.total_records} />
            <StatTile icon={UserX} label={t('admin.free_users')} value={stats.free_users} accent="text-muted-foreground" />
            <StatTile icon={Crown} label={t('admin.premium_users')} value={stats.premium_users} />
            <StatTile icon={Globe} label={t('admin.total_domains')} value={stats.total_domains} />
            <StatTile icon={Globe} label={t('admin.active_domains')} value={stats.active_domains} accent="text-emerald-500" />
          </div>
        )}

        {/* Tabs */}
        <div className="animate-fade-in-up delay-200" data-testid="admin-tabs">
          <div className="flex items-center gap-0.5 mb-4 border-b border-border/30">
            <button
              type="button"
              onClick={() => setActiveTab('users')}
              data-testid="admin-tab-users"
              className={`px-3 py-2 font-mono text-[11px] tracking-wider transition-colors border-b-2 ${
                activeTab === 'users'
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3 w-3" />
                {t('admin.users')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('domains')}
              data-testid="admin-tab-domains"
              className={`px-3 py-2 font-mono text-[11px] tracking-wider transition-colors border-b-2 ${
                activeTab === 'domains'
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Globe className="h-3 w-3" />
                {t('admin.domains')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('oauth')}
              data-testid="admin-tab-oauth"
              className={`px-3 py-2 font-mono text-[11px] tracking-wider transition-colors border-b-2 ${
                activeTab === 'oauth'
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <KeyRound className="h-3 w-3" />
                {t('admin.oauth_tab')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              data-testid="admin-tab-settings"
              className={`px-3 py-2 font-mono text-[11px] tracking-wider transition-colors border-b-2 ${
                activeTab === 'settings'
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <SettingsIcon className="h-3 w-3" />
                {t('admin.settings_tab')}
              </span>
            </button>
          </div>

          {/* Users */}
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-[10px] text-primary tracking-wider">$ users --list</div>
                <span className="font-mono text-[9px] text-muted-foreground">{users.length} entries</span>
              </div>
              <div className="rounded border border-border/40 bg-card/40 overflow-hidden">
                {users.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="no-users-msg">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs font-mono">{t('admin.no_users')}</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile */}
                    <div className="block sm:hidden divide-y divide-border/30">
                      {users.map((u) => (
                        <div key={u.id} data-testid={`admin-user-card-${u.id}`} className="p-4 space-y-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-mono truncate flex-1" dir="ltr">{u.email}</span>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground hover:text-primary" onClick={() => openUserRecords(u)} data-testid={`view-records-mobile-${u.id}`}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              {u.role !== 'admin' && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground" data-testid={`admin-actions-mobile-${u.id}`}>
                                      <MoreVertical className="h-3.5 w-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="rounded border-border/40 bg-popover/95 backdrop-blur-md">
                                    {u.plan === 'free' ? (
                                      <DropdownMenuItem onClick={() => handleChangePlan(u.id, 'premium')} className="text-[11px]">
                                        <Crown className="h-3 w-3 me-2" />{t('admin.set_premium')}
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem onClick={() => handleChangePlan(u.id, 'free')} className="text-[11px]">
                                        <UserX className="h-3 w-3 me-2" />{t('admin.set_free')}
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => { setDeleteUser(u); setDeleteOpen(true); }} className="text-destructive text-[11px]">
                                      <Trash2 className="h-3 w-3 me-2" />{t('admin.delete_user')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <PlanBadge plan={u.plan || 'free'} />
                            <RoleBadge role={u.role || 'user'} />
                            <span className="font-mono text-[10px] text-muted-foreground">→ {u.record_count || 0} {t('admin.records')}</span>
                          </div>
                          <p className="font-mono text-[10px] text-muted-foreground/70">{formatDate(u.created_at)}</p>
                        </div>
                      ))}
                    </div>
                    {/* Desktop */}
                    <div className="hidden sm:block">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border/30 bg-background/30">
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.email')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.plan')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.role')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.records')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.joined')}</th>
                            <th className="text-end px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-border/20 last:border-0 hover:bg-background/30 transition-colors">
                              <td className="px-4 py-3 font-mono text-xs" dir="ltr">{u.email}</td>
                              <td className="px-4 py-3"><PlanBadge plan={u.plan || 'free'} /></td>
                              <td className="px-4 py-3"><RoleBadge role={u.role || 'user'} /></td>
                              <td className="px-4 py-3 font-mono text-xs">{u.record_count || 0}</td>
                              <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{formatDate(u.created_at)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-0.5">
                                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground hover:text-primary" onClick={() => openUserRecords(u)} data-testid={`view-records-${u.id}`}>
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  {u.role !== 'admin' && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground" data-testid={`admin-actions-${u.id}`}>
                                          <MoreVertical className="h-3.5 w-3.5" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="rounded border-border/40 bg-popover/95 backdrop-blur-md">
                                        {u.plan === 'free' ? (
                                          <DropdownMenuItem onClick={() => handleChangePlan(u.id, 'premium')} data-testid={`set-premium-${u.id}`} className="text-[11px]">
                                            <Crown className="h-3 w-3 me-2" />{t('admin.set_premium')}
                                          </DropdownMenuItem>
                                        ) : (
                                          <DropdownMenuItem onClick={() => handleChangePlan(u.id, 'free')} data-testid={`set-free-${u.id}`} className="text-[11px]">
                                            <UserX className="h-3 w-3 me-2" />{t('admin.set_free')}
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => { setDeleteUser(u); setDeleteOpen(true); }} className="text-destructive text-[11px]" data-testid={`delete-user-${u.id}`}>
                                          <Trash2 className="h-3 w-3 me-2" />{t('admin.delete_user')}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Domains */}
          {activeTab === 'domains' && (
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-[10px] text-primary tracking-wider">$ domains --list</div>
                <Button size="sm" className="gap-1.5 rounded h-7 text-[10px] tracking-wider" onClick={() => setAddDomainOpen(true)} data-testid="add-domain-btn">
                  <Plus className="h-3 w-3" />
                  {t('admin.add_domain')}
                </Button>
              </div>
              <div className="rounded border border-border/40 bg-card/40 overflow-hidden">
                {domainsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : domains.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="no-domains-msg">
                    <Globe className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs font-mono">{t('admin.no_domains')}</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile */}
                    <div className="block sm:hidden divide-y divide-border/30">
                      {domains.map((d) => (
                        <div key={d.id} data-testid={`domain-card-${d.id}`} className="p-4 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs font-medium" dir="ltr">{d.name}</span>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={d.active}
                                onCheckedChange={() => handleToggleDomain(d)}
                                data-testid={`toggle-domain-mobile-${d.id}`}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded text-muted-foreground hover:text-destructive"
                                onClick={() => { setDeleteDomainItem(d); setDeleteDomainOpen(true); }}
                                data-testid={`delete-domain-mobile-${d.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground bg-background/50 border border-border/30 rounded px-2.5 py-1.5 break-all" dir="ltr">
                            <span className="text-primary/60">zone → </span>{d.zone_id}
                          </div>
                          <div className="flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              <span className={`w-1.5 h-1.5 rounded-full ${d.active ? 'bg-primary animate-pulse-dot' : 'bg-muted-foreground/30'}`} />
                              {d.active ? t('admin.domain_active') : t('admin.domain_inactive')}
                            </span>
                            <span>→ {d.record_count || 0} {t('admin.records')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop */}
                    <div className="hidden sm:block">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border/30 bg-background/30">
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.domain_name')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.zone_id')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.records')}</th>
                            <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.domain_active')}</th>
                            <th className="text-end px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('admin.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {domains.map((d) => (
                            <tr key={d.id} data-testid={`domain-row-${d.id}`} className="border-b border-border/20 last:border-0 hover:bg-background/30 transition-colors">
                              <td className="px-4 py-3 font-mono text-xs font-medium" dir="ltr">{d.name}</td>
                              <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground" dir="ltr">{d.zone_id.substring(0, 12)}…</td>
                              <td className="px-4 py-3 font-mono text-xs">{d.record_count || 0}</td>
                              <td className="px-4 py-3">
                                <Switch
                                  checked={d.active}
                                  onCheckedChange={() => handleToggleDomain(d)}
                                  data-testid={`toggle-domain-${d.id}`}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded text-muted-foreground hover:text-destructive"
                                    onClick={() => { setDeleteDomainItem(d); setDeleteDomainOpen(true); }}
                                    data-testid={`delete-domain-${d.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* OAuth */}
          {activeTab === 'oauth' && (
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-[10px] text-primary tracking-wider">$ oauth --google</div>
                <span className="font-mono text-[9px] text-muted-foreground">
                  {oauthForm.enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div className="rounded border border-border/40 bg-card/40 p-5 max-w-2xl">
                {oauthLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : (
                  <form onSubmit={saveOauthSettings} className="space-y-5">
                    <div>
                      <h3 className="font-heading text-sm font-bold flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-primary" />
                        {t('admin.oauth_title')}
                      </h3>
                      <p className="font-mono text-[10px] text-muted-foreground mt-1">// {t('admin.oauth_desc')}</p>
                    </div>

                    <div className="flex items-center justify-between rounded border border-border/40 bg-background/30 px-3 py-2.5">
                      <Label className="text-[11px] text-foreground/80">{t('admin.oauth_enabled')}</Label>
                      <Switch
                        checked={oauthForm.enabled}
                        onCheckedChange={(val) => setOauthForm({ ...oauthForm, enabled: val })}
                        data-testid="oauth-enabled-switch"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[10px] text-muted-foreground tracking-wider">{t('admin.oauth_client_id')}</Label>
                      <Input
                        value={oauthForm.client_id}
                        onChange={(e) => setOauthForm({ ...oauthForm, client_id: e.target.value })}
                        placeholder={t('admin.oauth_client_id_placeholder')}
                        data-testid="oauth-client-id-input"
                        className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                        dir="ltr"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[10px] text-muted-foreground tracking-wider">{t('admin.oauth_client_secret')}</Label>
                      <Input
                        type="password"
                        value={oauthForm.client_secret}
                        onChange={(e) => setOauthForm({ ...oauthForm, client_secret: e.target.value })}
                        placeholder={t('admin.oauth_client_secret_placeholder')}
                        data-testid="oauth-client-secret-input"
                        className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                        dir="ltr"
                      />
                      <p className="font-mono text-[10px] text-muted-foreground">→ {t('admin.oauth_hint')}</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={oauthSaving}
                      data-testid="oauth-save-btn"
                      className="rounded h-9 text-[11px] tracking-wider gap-1.5"
                    >
                      {oauthSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3 w-3" /> {t('admin.oauth_save')}</>}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-[10px] text-primary tracking-wider">$ system --settings</div>
              </div>
              <div className="rounded border border-border/40 bg-card/40 p-5 max-w-2xl">
                {appSettingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-heading text-sm font-bold flex items-center gap-2">
                        <SettingsIcon className="h-4 w-4 text-primary" />
                        {t('admin.settings_title')}
                      </h3>
                      <p className="font-mono text-[10px] text-muted-foreground mt-1">// {t('admin.settings_desc')}</p>
                    </div>

                    <div className="rounded border border-border/40 bg-background/30 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Label className="text-[12px] font-medium text-foreground/90 flex items-center gap-1.5">
                            <UserPlus className="h-3.5 w-3.5 text-primary/70" />
                            {t('admin.registration_toggle')}
                          </Label>
                          <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                            {t('admin.registration_toggle_desc')}
                          </p>
                        </div>
                        <Switch
                          checked={appSettings.registration_enabled}
                          disabled={appSettingsSaving}
                          onCheckedChange={(val) => saveAppSettings({ registration_enabled: val })}
                          data-testid="registration-toggle-switch"
                        />
                      </div>
                      <div className="flex items-center gap-1.5 font-mono text-[10px] pt-1 border-t border-border/30">
                        <span className={`w-1.5 h-1.5 rounded-full ${appSettings.registration_enabled ? 'bg-primary animate-pulse-dot' : 'bg-muted-foreground/40'}`} />
                        <span className={appSettings.registration_enabled ? 'text-primary' : 'text-muted-foreground'}>
                          {appSettings.registration_enabled ? 'enabled' : 'disabled'}
                        </span>
                        {appSettingsSaving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ms-auto" />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Domain Dialog */}
      <Dialog open={addDomainOpen} onOpenChange={setAddDomainOpen}>
        <DialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="add-domain-dialog">
          <DialogHeader>
            <div className="text-[10px] text-primary tracking-wider mb-1">$ domain --add</div>
            <DialogTitle className="font-heading text-base">{t('admin.add_domain')}</DialogTitle>
            <DialogDescription className="sr-only">{t('admin.add_domain')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDomain} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('admin.domain_name')}</Label>
              <Input
                value={domainForm.name}
                onChange={(e) => setDomainForm({ ...domainForm, name: e.target.value })}
                placeholder={t('admin.domain_name_placeholder')}
                required
                data-testid="domain-name-input"
                className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                dir="ltr"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('admin.zone_id')}</Label>
              <Input
                value={domainForm.zone_id}
                onChange={(e) => setDomainForm({ ...domainForm, zone_id: e.target.value })}
                placeholder={t('admin.zone_id_placeholder')}
                required
                data-testid="domain-zone-input"
                className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                dir="ltr"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded h-9 text-[11px]" onClick={() => setAddDomainOpen(false)}>
                {t('dashboard.cancel')}
              </Button>
              <Button type="submit" disabled={addDomainLoading} className="rounded h-9 text-[11px] tracking-wider" data-testid="domain-submit-btn">
                {addDomainLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('admin.add_domain')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <div className="text-[10px] text-destructive tracking-wider mb-1">$ rm -rf user</div>
            <AlertDialogTitle className="font-heading text-base">{t('admin.delete_user')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[11px]">
              {t('admin.delete_confirm')}
              {deleteUser && (
                <span className="block mt-2 font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2 py-1.5" dir="ltr">{deleteUser.email}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded h-9 text-[11px]">{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded h-9 text-[11px] tracking-wider"
              data-testid="confirm-delete-user-btn"
            >
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('admin.delete_user')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View User Records Dialog */}
      <Dialog open={recordsOpen} onOpenChange={setRecordsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="view-records-dialog">
          <DialogHeader>
            <div className="text-[10px] text-primary tracking-wider mb-1">$ user --records</div>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              {t('admin.view_records')}
            </DialogTitle>
            {recordsUser && (
              <p className="text-[11px] text-muted-foreground font-mono" dir="ltr">→ {recordsUser.email}</p>
            )}
            <DialogDescription className="sr-only">{t('admin.view_records')}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            {recordsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : userRecords.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-7 w-7 mx-auto mb-2 opacity-30" />
                <p className="text-xs font-mono">{t('admin.no_records')}</p>
              </div>
            ) : (
              <div className="space-y-2 p-1">
                {userRecords.map((record) => (
                  <div key={record.id} data-testid={`admin-record-${record.id}`} className="rounded border border-border/40 bg-background/30 p-3">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="flex items-center gap-2 min-w-0" dir="ltr">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-border/50 bg-background/40 font-mono text-[9px] tracking-wider">{record.record_type}</span>
                        <span className="font-mono text-xs truncate">
                          {record.name}<span className="text-muted-foreground">.{record.domain_name || 'dnslab.biz'}</span>
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => { setDeleteRecordItem(record); setDeleteRecordOpen(true); }}
                        data-testid={`admin-delete-record-${record.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2 py-1.5 break-all" dir="ltr">
                      <span className="text-primary/60">→ </span>{record.content}
                    </div>
                    <div className="flex items-center gap-3 mt-2 font-mono text-[10px] text-muted-foreground">
                      <span>ttl: {record.ttl === 1 ? 'auto' : record.ttl}</span>
                      <span className="inline-flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${record.proxied ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                        {record.proxied ? 'proxied' : 'dns-only'}
                      </span>
                      {record.created_at && <span>{formatDate(record.created_at)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Record Confirmation */}
      <AlertDialog open={deleteRecordOpen} onOpenChange={setDeleteRecordOpen}>
        <AlertDialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="admin-delete-record-dialog">
          <AlertDialogHeader>
            <div className="text-[10px] text-destructive tracking-wider mb-1">$ rm -f record</div>
            <AlertDialogTitle className="font-heading text-base">{t('admin.delete_record')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[11px]">
              {t('admin.delete_record_confirm')}
              {deleteRecordItem && (
                <span className="block mt-2 font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2 py-1.5" dir="ltr">
                  {deleteRecordItem.name}.{deleteRecordItem.domain_name || 'dnslab.biz'} ({deleteRecordItem.record_type}) → {deleteRecordItem.content}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded h-9 text-[11px]">{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRecord}
              disabled={deleteRecordLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded h-9 text-[11px] tracking-wider"
              data-testid="admin-confirm-delete-record-btn"
            >
              {deleteRecordLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('dashboard.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Domain Confirmation */}
      <AlertDialog open={deleteDomainOpen} onOpenChange={setDeleteDomainOpen}>
        <AlertDialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="delete-domain-dialog">
          <AlertDialogHeader>
            <div className="text-[10px] text-destructive tracking-wider mb-1">$ rm -f domain</div>
            <AlertDialogTitle className="font-heading text-base">{t('admin.delete_domain')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[11px]">
              {t('admin.delete_domain_confirm')}
              {deleteDomainItem && (
                <span className="block mt-2 font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2 py-1.5" dir="ltr">{deleteDomainItem.name}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded h-9 text-[11px]">{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDomain}
              disabled={deleteDomainLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded h-9 text-[11px] tracking-wider"
              data-testid="confirm-delete-domain-btn"
            >
              {deleteDomainLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('admin.delete_domain')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
